<template>
  <div>
    <v-sheet class="clipList_main">
      <div class="clipList_title">
        <div class="clipList_title_text">
          {{ tagName || 'ビデオクリップ一覧' }}
        </div>
      </div>
      <div class="clipList_events_container scrollParent">
        <div class="clipList_events scrollChild">
          <app-skeleton
            :loading="loadingClips && videoClips.length === 0"
            :nodata="videoClips.length === 0"
            nodata-text="該当するビデオクリップはありません"
            skeleton="homeEvents"
          />
          <ClipItem
            v-for="(clip, index) in videoClips"
            :key="`clip-list-clip-item_${clip.id}-${index}`"
            :clip="clip"
            :hide-link="clipId == clip.id"
          />
          <infinite-loading
            v-if="videoClips.length > 0"
            ref="infinitScroll"
            spinner="spiral"
            force-use-infinite-wrapper=".clipList_events.scrollChild"
            @infinite="loadMoreClips"
          >
            <div slot="no-more"></div>
            <div slot="no-results"></div
          ></infinite-loading>
        </div>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import EventService from '@/services/event.service';
import ClipItem from '@/components/clips/ClipItem.vue';
import { uuidv4 } from '@/utils/helpers';
import InfiniteLoading from 'vue-infinite-loading';
import StorageService from '@/services/StorageService';
import { STORAGE_KEYS } from '@/constants/storage';
import CameraService from '@/services/CameraService';

export default {
  name: 'ClipList',
  components: {
    ClipItem,
    InfiniteLoading,
  },
  data: () => ({
    eventService: new EventService(),
    loading: false,
    limit: 50,
    searchFilterParams: {},
    infinitScrollState: null,
    currentOffset: 0,
    getClipsReqId: null,
    loadingClips: false,
    currentListHasMore: false,
  }),
  computed: {
    ...mapState({
      clip: (state) => state.video.videoClip,
      clipTags: (state) => state.video.clipTags,
      videoClips: (state) =>
        state.video.videoClips.map((item, index) => ({ ...item, index })),
    }),
    clipId() {
      return Number(this.$route.params.id);
    },
    tagId() {
      return this.$route.params.tag;
    },
    isTag() {
      return !!this.tag;
    },
    tag() {
      return this.tagId
        ? this.clipTags.find((item) => String(item.id) === String(this.tagId))
        : null;
    },
    clipName() {
      return this.clip ? this.clip.clipName : '';
    },
    tagName() {
      return this.tag ? this.tag.name : null;
    },
    fromClipPage() {
      return this.$route.params.fromClipPage;
    },
  },
  watch: {
    async tagId() {
      this.currentOffset = 0;
      this.loadingClips = true;
      this.resetSearchClips();
      await this.loadMoreClips();
      this.loadingClips = false;
      this.setClipListLoading(false);
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    ...mapActions('video', [
      'fetchVideoClips',
      'fetchVideoClipsToTargetClip',
      'resetSearchClips',
      'fetchClipTags',
      'setClipList',
      'createTag',
      'updateVideoClip',
      'moveToTag',
      'checkEmptyTagToDelete',
      'updateTag',
      'setClipListLoading',
      'setSearchParams',
    ]),
    ...mapActions('common', ['abortHttpRequest']),
    async init() {
      try {
        const params = StorageService.getItem(
          STORAGE_KEYS.LastVideoClipsSearchParams
        );
        this.setClipListLoading(true);
        let targetClip = null;
        if (params?.offset) {
          this.limit =
            CameraService.getClipListPosition() || params.offset + 50;
          this.loadingClips = true;
          this.resetSearchClips();
          this.currentListHasMore = await this.getClips(true);
          this.limit = 50;
          this.currentOffset =
            CameraService.getClipListPosition() > 0
              ? CameraService.getClipListPosition() + 50
              : params.offset + this.limit;
        }
        targetClip = this.videoClips.find((c) => +c.id === +this.clipId);

        // if target clip does not exist in the list
        if (!targetClip) {
          this.resetSearchClips();
          const { hasMore, offset, limit } =
            await this.fetchVideoClipsToTargetClip({
              clipId: this.clipId,
            });
          CameraService.saveClipSearchParams({
            upperLimit: limit,
            offset,
            tagIds: [],
          });
          this.currentOffset = offset;
          this.currentListHasMore = hasMore;
          this.limit = limit;
        }

        this.scrollToActiveItem();
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingClips = false;
        this.setClipListLoading(false);
      }
    },
    scrollToActiveItem() {
      const currentActiveClip =
        this.$el.getElementsByClassName('clipItem_active');
      if (currentActiveClip && currentActiveClip[0]) {
        currentActiveClip[0].scrollIntoView();
      }
    },
    abortGetClipsRquest() {
      if (this.getClipsReqId) {
        this.abortHttpRequest(this.getClipsReqId);
      }
    },
    handleSearch(filterParams) {
      this.searchFilterParams = filterParams;
      this.currentOffset = 0;
      if (this.$refs.infinitScroll && this.$refs.infinitScroll.stateChanger) {
        this.$refs.infinitScroll.stateChanger.reset();
      }
      this.resetSearchClips();
    },
    async loadMoreClips($state) {
      if (this.currentListHasMore) {
        this.infinitScrollState = $state;
        const hasMore = await this.getClips();
        if (hasMore) {
          this.currentOffset += this.limit;
          this.$refs.infinitScroll &&
            this.$refs.infinitScroll.stateChanger.loaded();
        } else {
          this.$refs.infinitScroll.stateChanger.complete();
        }
      } else {
        this.$refs.infinitScroll.stateChanger.complete();
      }
    },
    async getClips(init = false) {
      this.abortGetClipsRquest();
      const searchParams = {
        upperLimit: this.limit,
        offset: this.currentOffset,
        tagIds: [],
        ...this.searchFilterParams,
      };
      if (this.tag) {
        searchParams.tagIds = [this.tag.id];
      } else if (this.tagId) {
        searchParams.tagIds = [this.tagId];
      }
      searchParams.init = init;
      this.getClipsReqId = uuidv4().replaceAll('-', '');
      Object.setPrototypeOf(searchParams, { __reqId: this.getClipsReqId });
      const { hasMore } = await this.fetchVideoClips(searchParams);
      return hasMore;
    },
  },
};
</script>

<style lang="scss">
.clipList {
  &_notification {
    &.v-sheet.theme--light {
      .clipList & {
        flex-grow: 0;
        margin-bottom: 30px;
        color: #fff;
        background-color: var(--primary-color);
        padding-bottom: 16px;
        box-shadow: 0 3px 12px 0 rgba(0, 45, 170, 0.19);
      }
    }
    &_supTitle {
      font-size: 14px;
      font-weight: 600;
      line-height: 17px;
      margin-bottom: 7px;
    }
    &_title {
      font-size: 16px;
      font-weight: 600;
      line-height: 21px;
      margin-bottom: 2px;
    }
    .eventItem {
      border-top: 0;
      padding-left: 0;
      padding-right: 0;
    }
    .eventItem_time {
      color: #fff;
    }
    .eventItem_date {
      font-weight: 400;
    }
    .eventItem_hour {
      font-weight: 400;
    }
    .eventItem_chevron {
      display: none;
    }
  }
  &_main {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    &.v-sheet {
      padding-right: 22px;
    }
  }
  &.v-sheet {
    padding-right: 22px;
  }
  &_title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 17px;
    padding-right: 8px;
    &_text {
      font-size: 14px;
      font-weight: 600;
      color: var(--text-color);
    }
    &_btn {
      .icon {
        margin-left: 8px;
        margin-right: -5px;
        position: relative;
      }
    }
    &_icon {
      &-open {
        transform: rotate(180deg);
      }
    }
  }
  &_search {
    max-height: 0;
    margin: 0 -22px 0 -30px;
    padding: 0 30px;
    transition: max-height 0.2s cubic-bezier(0.25, 0.8, 0.5, 1);
    overflow: hidden;
    &-open {
      max-height: 390px;
    }
  }
  &_events {
    padding-right: 8px;
    &_container {
      &:before {
        content: '';
        position: absolute;
        top: 0;
        right: 8px;
        left: 0;
        height: 1px;
        background: #ccc;
        z-index: 3;
      }
      &:after {
        content: '';
        position: absolute;
        background-color: #d1d2d8;
        bottom: 0;
        left: 0;
        right: 8px;
        height: 1px;
      }
    }
  }
}
</style>
